import React from 'react';
import { Bundle } from '@ali/visualengine';
import {
  createFilterSelectSetters,
  getArraySetterSnippet,
  getCnSelectSetter,
  getDataOriginSetterSnippet,
  getMixedSetterSnippet,
  getObjectSetterSnippet,
  getRequestConfigSetterSnippet,
  getNodeIdSetterSnippet,
  createDataSourceSetters,
  getJSXTemplate,
} from '@/common/manager/setter-snippet';
import { getJSExpressionPrototype } from '@/common/manager/common-style';
import { condition, uuid } from '@/common/util/util';
import { dataOriginRequest, dataOriginStatic } from '@/common/util/const';
import { createEventSetters } from '@/common/manager/event';
import { ButtonPosition } from '@/type/button-position';
import { DisplayPosition } from '@/type/display-position';
import TipSetter from '@/common/setter/tip-setter';

// 原型配置请参考：https://lark.alipay.com/vision/docs/prototype
export default Bundle.createPrototype({
  title: '列表',
  componentName: 'CnList',
  category: 'FTP',
  snippets: [
    {
      screenshot:
        'https://img.alicdn.com/imgextra/i3/O1CN01W7vuXp1nb3ZH84PBw_!!6000000005107-0-tps-712-420.jpg',
      label: 'CnList',
      schema: {
        componentName: 'CnList',
        props: {},
      },
    },
  ],
  isContainer: false,
  canHovering: true,
  canSelecting: true,
  canDragging: true,
  enableCopy: true,
  configure: [
    {
      name: 'isCnList',
      title: 'isCnList',
      initialValue: true,
      display: 'none',
    },
    {
      name: '_context',
      title: '上下文',
      initialValue: {
        type: 'JSExpression',
        value: 'this',
      },
      display: 'none',
    },
    {
      name: 'title',
      title: '列表名称',
      display: 'inline',
      initialValue: '列表1',
      setter: 'StringSetter',
    },
    {
      name: 'dataFrom',
      title: '数据来源',
      setter: getObjectSetterSnippet({
        items: [
          getDataOriginSetterSnippet({
            display: 'inline',
            title: '数据类型',
          }),
          getRequestConfigSetterSnippet(
            {
              condition: (prop) => {
                return prop?.parent?.getPropValue?.('dataOrigin') === 'request';
              },
            },
            {
              extraServiceProps: {
                resultProcessFuncTemplate: `function(res) {
  // 需要返回的如下的数据结构
  // return {
  //   success: true,
  //   data: {
  //     tableData: [
  //       {
  //         mediaImgUrl:"xx",
  //         title:"xx",
  //         subTitle:"xx",
  //         description:"xx",
  //         extra:"xx",
  //         arrow:"xx",
  //         key:"xx",
  //       }
  //     ],
  //     paging: {
  //       pageSize: 10,
  //       totalCount: 30,
  //       currentPage: 1
  //     }
  //   }
  // }
 return res;
}`,
                responseDom: (
                  <div style={{ paddingTop: '10px' }}>
                    请求返回结果的数据结构：
                    <a
                      target={'_blank'}
                      href='https://cone.cainiao-inc.com/dsm/common/material/mobile/CnList'
                      rel='noreferrer'
                    >
                      数据结构参考 CnListItem 参数
                    </a>
                  </div>
                )
              },
            },
          ),
          {
            name: 'dataSource',
            title: '编辑静态数据',
            display: 'plain',
            tip: '编辑静态展示数据',
            setter: getMixedSetterSnippet({
              setters: [
                getArraySetterSnippet({
                  initialValue: () => {
                    return {
                      title: '标题',
                      subTitle: '副标题',
                      description: '描述',
                      extra: '说明',
                      arrow: 'right',
                      primaryKey: uuid(6),
                    };
                  },
                  configure: [
                    {
                      name: 'title',
                      title: '标题',
                      setter: 'CnI18nSetter',
                      isRequired: true,
                    },
                    {
                      name: 'subTitle',
                      title: '副标题',
                      setter: 'CnI18nSetter',
                      isRequired: true,
                    },
                    {
                      name: 'primaryKey',
                      title: '主键',
                      setter: 'StringSetter',
                    },
                    {
                      name: 'description',
                      title: '描述',
                      setter: 'CnI18nSetter',
                    },
                    {
                      name: 'extra',
                      title: '说明',
                      setter: 'CnI18nSetter',
                    },
                    {
                      name: 'mediaImgUrl',
                      title: '图片链接',
                      setter: 'StringSetter',
                    },
                    {
                      name: 'arrow',
                      title: '箭头方向',
                      setter: getCnSelectSetter({
                        options: [
                          { value: undefined, label: '无' },
                          { value: 'left', label: '左' },
                          { value: 'right', label: '右' },
                          { value: 'up', label: '上' },
                          { value: 'down', label: '下' },
                        ],
                      }),
                    },
                  ],
                }),
              ],
            }),
            condition(prop) {
              return (
                prop?.parent?.getPropValue?.('dataOrigin') === dataOriginStatic
              );
            },
          },
        ],
      }),
    },
    {
      title: '样式',
      type: 'group',
      display: 'accordion',
      collapsed: true,
      items: [
        {
          name: 'showBorderRadius',
          title: '是否显示圆角',
          display: 'inline',
          defaultValue: false,
          setter: 'BoolSetter',
        },
        {
          name: 'itemDescriptionRender',
          display: 'inline',
          title: '描述自定义渲染',
          setter: getMixedSetterSnippet({
            setters: [
              <TipSetter tip={'请点击右侧切换按钮写JS表达式'} />,
              getJSXTemplate({
                position: DisplayPosition.listItemDescriptionRender,
              }),
            ],
          }),
        },
        {
          name: 'pagging',
          display: 'inline',
          title: '展示总页数',
          setter: 'BoolSetter',
        },
        // {
        //   name: 'itemContentRender',
        //   display: 'inline',
        //   title: '内容自定义渲染',
        //   setter: getMixedSetterSnippet({
        //     setters: [
        //       <TipSetter tip={'请点击右侧切换按钮写JS表达式'} />,
        //       getJSXTemplate({
        //         position: DisplayPosition.listItemContentRender,
        //       }),
        //     ],
        //   }),
        // },
      ],
    },
    ...createFilterSelectSetters({
      mode: 'multiple',
    }),
    ...createEventSetters({
      position: ButtonPosition.listItemClick,
      initialValue: {
        optType: 'flowAction',
      },
      title: '配置列表的回调',
    }),
    {
      title: '高级配置',
      type: 'group',
      collapsed: true,
      display: 'accordion',
      items: [
        getNodeIdSetterSnippet(),
        {
          name: 'handleProps',
          title: '手动设置组件props',
          display: 'inline',
          setter: getJSExpressionPrototype({
            type: 'props',
          }),
        },
        ...createDataSourceSetters(),
      ],
    },
  ],
});
